export interface APICitation {
  index: number;
  is_used: boolean;
  title: string;
  url: string;
  snippet?: string;
  score?: number;
}

export interface SessionStartEvent {
  event: "session_start";
  session_id: string;
  timestamp: string;
}

export interface CitationsEvent {
  event: "citations";
  citations: APICitation[];
}

export interface OtherLinksEvent {
  event: "other_links";
  links: APICitation[];
}

export interface ContentDeltaEvent {
  event: "content_delta";
  content: string;
}

export interface ContentCompleteEvent {
  event: "content_complete";
  content: string;
}

export interface FollowupQuestion {
  id: string;
  index: number;
  question: string;
}

export interface FollowupQuestionsEvent {
  event: "followup_questions";
  questions: FollowupQuestion[];
}

export interface ResponseMetadataEvent {
  event: "response_metadata";
  id: string;
  selected_agent: string;
  selected_agent_display_name?: string;
  recommendor_response?: string;
  fallback_agent?: string;
  is_system_fallback?: boolean;
  created_at: string;
  list_all_citations?: boolean;
}

export interface ErrorEvent {
  event: "error";
  error: string;
  code: string;
  recoverable?: boolean;
}

export type NDJSONEvent =
  | SessionStartEvent
  | CitationsEvent
  | OtherLinksEvent
  | ContentDeltaEvent
  | ContentCompleteEvent
  | FollowupQuestionsEvent
  | ResponseMetadataEvent
  | ErrorEvent;

export function isCitationsEvent(event: unknown): event is CitationsEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "citations" &&
    "citations" in event &&
    Array.isArray((event as CitationsEvent).citations) &&
    (event as CitationsEvent).citations.length > 0
  );
}

export function isOtherLinksEvent(event: unknown): event is OtherLinksEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "other_links" &&
    "links" in event &&
    Array.isArray((event as OtherLinksEvent).links)
  );
}

export function isContentDeltaEvent(event: unknown): event is ContentDeltaEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "content_delta" &&
    "content" in event &&
    typeof (event as ContentDeltaEvent).content === "string"
  );
}

export function isContentCompleteEvent(event: unknown): event is ContentCompleteEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "content_complete" &&
    "content" in event
  );
}

export function isFollowupQuestionsEvent(event: unknown): event is FollowupQuestionsEvent {
  if (
    typeof event !== "object" ||
    event === null ||
    !("event" in event) ||
    (event as Record<string, unknown>).event !== "followup_questions" ||
    !("questions" in event)
  ) {
    return false;
  }

  const questions = (event as FollowupQuestionsEvent).questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    return false;
  }

  return questions.every(
    q =>
      typeof q === "object" &&
      q !== null &&
      typeof q.id === "string" &&
      typeof q.index === "number" &&
      typeof q.question === "string" &&
      q.question.trim().length > 0
  );
}

// NOTE: isTokenUsageEvent removed — token usage is recorded server-side via OTel only.

export function isResponseMetadataEvent(event: unknown): event is ResponseMetadataEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "response_metadata" &&
    "id" in event &&
    "selected_agent" in event &&
    "created_at" in event
  );
}

export function isErrorEvent(event: unknown): event is ErrorEvent {
  return (
    typeof event === "object" &&
    event !== null &&
    "event" in event &&
    (event as Record<string, unknown>).event === "error" &&
    "error" in event &&
    "code" in event
  );
}
